import { useCallback, useEffect, useState, useTransition, useMemo, useRef } from 'react';
import debounce from 'lodash.debounce';
import { ContentContainer } from '../../../components/Filters/components/ContentContainer';
import { ControlsPanel } from '../../../components/Filters/components/ControlsPanel';
import { CheckboxList } from '../../../components/Filters/components/CheckboxList';
import { SearchInput } from '../../../components/Filters/components/SearchInput';
import { ItemBadgesContainer } from '../../../components/Filters/components/ItemBadgesContainer';
import { FilterLoader } from '../../../components/Filters/components/FilterLoader';
import { NoResults } from '../../../components/Filters/components/NoResults';
import { API } from '../../../API';
import { showRequestError } from '../../../utils/requestError';
import { useTranslation } from 'react-i18next';
import { useSearchContext } from '../contexts';


export interface CountryOption {
  value: number | string;
  label: string;
}

export interface CountriesResponse {
  objects: CountryData[];
  total_count: number;
}


export interface CountryData {
  id: number;
  name: string;
  code: string;
  sources_count: number;
}

export const AffiliationCountryChooser = () => {
  const { t } = useTranslation();
  const { state, updateDraftFilters } = useSearchContext();
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [defaultOptions, setDefaultOptions] = useState<CountryOption[]>([]);
  const [allLoadedCountries, setAllLoadedCountries] = useState<CountryOption[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isPending, startTransition] = useTransition();

  const isAllSelected = state.draftFilters.affiliations.value.length > 0 && state.draftFilters.affiliations.value.includes('all');
  // Use draft filters from context - need to convert string[] to number[]
  const selectedCountries: (string | number)[] = isAllSelected ? ['all'] : state.draftFilters.affiliations.value.map(c => parseInt(c));

  const getAllCountries = useCallback(() => {
      startTransition(() => {
        API.fetch('GET', `/API/v1/countries`)
          .then((data: CountriesResponse) => {
            let optionsList = data.objects
              .slice(0, 10)
              .map((country: CountryData) => {
                return {value: country.id, label: country.name};
              })
              setDefaultOptions(optionsList)
              // Store all loaded countries
              setAllLoadedCountries(prev => {
                const existingIds = new Set(prev.map(c => c.value));
                const newCountries = optionsList.filter(c => !existingIds.has(c.value));
                return [...prev, ...newCountries];
              });
          })
          .catch((error) => {
            showRequestError(error, t('Countries'));
          });
      });
    }, []);

    useEffect(getAllCountries, [getAllCountries]);

    // Fetch selected countries that aren't in default options on mount
    useEffect(() => {
      if (selectedCountries.length === 0 || isAllSelected) return;

      // Get IDs that need to be fetched
      const missingIds = selectedCountries.filter(id =>
        !allLoadedCountries.some(c => c.value === id)
      );

      if (missingIds.length === 0) return;

      // Fetch missing countries
      missingIds.forEach(countryId => {
        API.fetch('GET', `/API/v1/countries/${countryId}`)
          .then((country: CountryData) => {
            const countryOption = {
              value: country.id,
              label: country.name
            };
            setAllLoadedCountries(prev => {
              // Double check it wasn't added by another concurrent request
              if (prev.some(c => c.value === country.id)) {
                return prev;
              }
              return [...prev, countryOption];
            });
          })
          .catch((error) => {
            showRequestError(error, t('Countries'));
          });
      });
    }, [selectedCountries.length, isAllSelected]); // Only run when selection count changes or on mount

    const getCountries = useCallback((inputValue: string) => {
        if (!inputValue) {
          setCountries([]);
          return;
        }

        startTransition(() => {
          API.fetch('GET', `/API/v1/countries?q=${inputValue}`)
            .then((data: CountriesResponse) => {
              let optionsList = data.objects
                .map((country: CountryData) => {
                  return {value: country.id, label: country.name};
                })
              setCountries(optionsList);
              // Store all loaded countries
              setAllLoadedCountries(prev => {
                const existingIds = new Set(prev.map(c => c.value));
                const newCountries = optionsList.filter(c => !existingIds.has(c.value));
                return [...prev, ...newCountries];
              });
            })
            .catch((error) => {
              showRequestError(error, t('Countries'));
            });
        });
      }, []);

    const debouncedSearchRef = useRef(
      debounce((value: string) => {
        getCountries(value);
      }, 500)
    );

    // Update the debounced function when getCountries changes
    useEffect(() => {
      debouncedSearchRef.current = debounce((value: string) => {
        getCountries(value);
      }, 500);
    }, [getCountries]);

    useEffect(() => {
      const debouncedSearch = debouncedSearchRef.current;
      return () => {
        debouncedSearch.cancel();
      };
    }, []);

    const handleSearchChange = (value: string) => {
      setSearchValue(value);
      debouncedSearchRef.current(value);
    };

  const handleClearAll = () => {
    updateDraftFilters({
      affiliations: {
        value: [],
        exclude: false
      }
    });
  };

  const handleToggleCountry = useCallback((countryId: number | string) => {
    const currentCountries = state.draftFilters.affiliations.value;
    const countryIdStr = String(countryId);

    if (currentCountries.includes(countryIdStr)) {
      updateDraftFilters({
        affiliations: {
          value: currentCountries.filter(id => id !== countryIdStr),
          exclude: state.draftFilters.affiliations.exclude
        }
      });
    } else {
      updateDraftFilters({
        affiliations: {
          value: [...currentCountries, countryIdStr],
          exclude: state.draftFilters.affiliations.exclude
        }
      });
    }
  }, [state.draftFilters.affiliations, updateDraftFilters]);


  const listToShow = countries.length > 0 ? countries : defaultOptions;
  const isNoResults = !isPending && searchValue && countries.length === 0;

  const checkboxItems = useMemo(() => {
    return listToShow.map((country: CountryOption) => ({
      name: country.label,
      checked: selectedCountries.includes(typeof country.value === 'string' ? parseInt(country.value) : country.value),
      onChange: () => handleToggleCountry(country.value),
      disabled: false,
      icon: null,
    }));
  }, [listToShow, selectedCountries, handleToggleCountry]);

  const badges = useMemo(() => {
    if (isAllSelected) {
      return [{
        name: 'All',
        onChange: () => updateDraftFilters({
          affiliations: {
            value: [],
            exclude: state.draftFilters.affiliations.exclude
          }
        })
      }];
    }
    return selectedCountries.map(countryId => {
      const country = allLoadedCountries.find((c: CountryOption) => c.value === countryId);
      const countryName = country ? country.label : String(countryId);
      return {
        name: countryName,
        onChange: () => handleToggleCountry(countryId)
      };
    });
  }, [isAllSelected, selectedCountries, allLoadedCountries, handleToggleCountry, updateDraftFilters, state.draftFilters.affiliations.exclude]);

  return(
    <ContentContainer
      searchComponent={
        <SearchInput
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={t('Search countries')}
          hint={t('Use search to find options not shown in the list')}
          id='affiliation-country-search'
          isDisabled={isAllSelected}
        />
      }
      actionsComponent={
        <ControlsPanel
          id='affiliation-country-filter'
          count={selectedCountries.length}
          isExclude={state.draftFilters.affiliations.exclude}
          showExcludeSwitch={true}
          showSelectAll={true}
          onClearAll={handleClearAll}
          isSelectAllDisabled={isAllSelected}
          onExcludeClick={() => {
            updateDraftFilters({
              affiliations: {
                value: state.draftFilters.affiliations.value,
                exclude: !state.draftFilters.affiliations.exclude
              }
            });
          }}
          onSelectAll={() => {
            updateDraftFilters({
              affiliations: {
                value: ['all'],
                exclude: state.draftFilters.affiliations.exclude
              }
            });
          }}
        />
      }
      badgesComponent={
        selectedCountries.length > 0 ? (
          <ItemBadgesContainer badges={badges} />
        ) : undefined
      }
    >
      {isPending ? <FilterLoader /> : isNoResults ? <NoResults /> : isAllSelected ? null : (
        <CheckboxList items={checkboxItems} />
      )}
    </ContentContainer>
  )
};
